/**
 * The Config sheet — the far end's whole radio setup, off the data screens (the deck stays
 * minimal). Assembles a `cfg` command from pickers with a live preview, plus the ranging
 * controls (tofdelay, arm/disarm subordinate) and the GPS-logging switch. All commands go
 * out through `onSend`; the firmware validates them (REQ-CON-9). Built on demand and
 * removed on close, like the Sessions sheet.
 */
import {
  BANDS,
  SF_VALUES,
  BW_VALUES,
  CR_VALUES,
  SYNC_VALUES,
  bandOf,
  clampPwr,
  buildCfg,
  cfgWarnings,
  type Band,
  type CfgState,
} from '../cfg';

export type ConfigState = {
  cfg: CfgState;
  tofdelay: number;
  /** The session power ceiling, `min(pwr, pwrmax)` at the board (PROTOCOL.md §2.2). */
  pwrmax: number;
  gpsEnabled: boolean;
  connected: boolean; // gate the send actions; GPS toggle works regardless
};

export type ConfigHandlers = {
  onApply(cfg: CfgState): void; // app stores the state and sends buildCfg
  onSetTofdelay(value: number): void; // app stores and sends cfg tofdelay=
  onSetPwrmax(value: number): void; // app stores and sends cfg pwrmax=
  onSend(text: string, src: 'macro' | 'typed'): void; // arm/disarm
  onToggleGps(enabled: boolean): void;
};

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string,
): HTMLElementTagNameMap[K] => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

function select(values: readonly (number | string)[], current: number | string): HTMLSelectElement {
  const s = el('select', 'field select');
  for (const v of values) {
    const o = el('option', undefined, String(v));
    o.value = String(v);
    s.append(o);
  }
  s.value = String(current); // set on the element, not via option.selected (DOM-impl robust)
  return s;
}

function labeled(tag: string, control: HTMLElement): HTMLElement {
  const wrap = el('label', 'cfg-field');
  wrap.append(el('span', 'cfg-lbl', tag), control);
  return wrap;
}

export function openConfigSheet(state: ConfigState, h: ConfigHandlers): void {
  const s: CfgState = { ...state.cfg };
  let band: Band = bandOf(s.freq);

  const backdrop = el('div', 'sheet-backdrop');
  const sheet = el('div', 'sheet');
  const close = () => backdrop.remove();
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });
  sheet.append(el('h2', undefined, 'Config'));

  // --- band ---------------------------------------------------------------
  const bandRow = el('div', 'cfg-band');
  const b868 = el('button', 'btn small', '868');
  const b24 = el('button', 'btn small', '2.4G');
  b868.type = 'button';
  b24.type = 'button';
  bandRow.append(b868, b24);

  // --- fields -------------------------------------------------------------
  const freq = el('input', 'field');
  freq.type = 'text';
  freq.inputMode = 'numeric';
  freq.value = String(s.freq);
  const sf = select(SF_VALUES, s.sf);
  const bw = select(BW_VALUES, s.bw);
  const cr = select(CR_VALUES, s.cr);
  const pwr = el('input', 'field');
  pwr.type = 'text';
  pwr.inputMode = 'numeric';
  pwr.value = String(s.pwr);
  const pwrHint = el('span', 'cfg-hint', '');
  const sync = select(
    SYNC_VALUES.map((v) => v.value),
    s.sync,
  );
  // Label sync options by name.
  [...sync.options].forEach((o, i) => (o.textContent = SYNC_VALUES[i]!.label));

  const freqField = labeled('freq (Hz)', freq);
  const grid = el('div', 'cfg-grid');
  grid.append(labeled('sf', sf), labeled('bw (kHz)', bw), labeled('cr', cr), labeled('pwr (dBm)', pwr));
  const syncField = labeled('sync', sync);

  // --- preview + apply ----------------------------------------------------
  const preview = el('div', 'cfg-preview');
  const warn = el('div', 'cfg-warn hidden');
  const apply = el('button', 'btn send cfg-apply', 'APPLY CONFIG');
  apply.type = 'button';

  const read = (): CfgState => ({
    freq: Number(freq.value) || 0,
    sf: Number(sf.value),
    bw: Number(bw.value),
    cr: cr.value,
    pwr: Number(pwr.value) || 0,
    sync: Number(sync.value),
  });
  const render = () => {
    band = bandOf(read().freq);
    b868.classList.toggle('active', band === '868');
    b24.classList.toggle('active', band === '2.4G');
    const b = BANDS[band];
    pwrHint.textContent = `${band} band · power ${b.pwrMin}…${b.pwrMax} dBm`;
    preview.textContent = buildCfg(read());
    const warnings = cfgWarnings(read());
    warn.textContent = warnings.length ? `⚠ ${warnings.join(' · ')}` : '';
    warn.classList.toggle('hidden', warnings.length === 0);
  };
  for (const c of [freq, sf, bw, cr, pwr, sync]) c.addEventListener('input', render);
  b868.addEventListener('click', () => {
    freq.value = String(BANDS['868'].freq);
    render();
  });
  b24.addEventListener('click', () => {
    freq.value = String(BANDS['2.4G'].freq);
    render();
  });
  apply.addEventListener('click', () => {
    const st = read();
    st.pwr = clampPwr(st.pwr, bandOf(st.freq)); // never send an out-of-band power
    pwr.value = String(st.pwr);
    render();
    h.onApply(st);
  });

  // --- power ceiling ------------------------------------------------------
  // Its own field and its own button, following tofdelay. Deliberately NOT an APPLY key
  // (07 §9.1): the board stores pwrmax as typed and never widens it on its own, and folding it
  // into buildCfg would let one tap of APPLY undo a limit the operator set on purpose.
  const ceilingHead = el('div', 'cfg-section', 'Power ceiling');
  const pwrmax = el('input', 'field');
  pwrmax.type = 'text';
  pwrmax.inputMode = 'numeric';
  pwrmax.value = String(state.pwrmax);
  const setMax = el('button', 'btn small', 'SET');
  setMax.type = 'button';
  setMax.addEventListener('click', () => h.onSetPwrmax(Number(pwrmax.value) || 0));
  const ceilingRow = el('div', 'cfg-delay');
  ceilingRow.append(el('span', 'cfg-lbl', 'pwrmax'), pwrmax, setMax);
  const ceilingNote = el(
    'div',
    'cfg-hint',
    'The board transmits at min(pwr, pwrmax), then band-clamps. Sent on its own, never by APPLY.',
  );

  // --- ranging ------------------------------------------------------------
  const rangingHead = el('div', 'cfg-section', 'Ranging');
  const tofdelay = el('input', 'field');
  tofdelay.type = 'text';
  tofdelay.inputMode = 'numeric';
  tofdelay.value = String(state.tofdelay);
  const setDelay = el('button', 'btn small', 'SET');
  setDelay.type = 'button';
  setDelay.addEventListener('click', () => h.onSetTofdelay(Number(tofdelay.value) || 0));
  const delayRow = el('div', 'cfg-delay');
  delayRow.append(el('span', 'cfg-lbl', 'tofdelay'), tofdelay, setDelay);

  const arm = el('button', 'btn small', 'Arm subordinate');
  const disarm = el('button', 'btn small warn', 'Disarm');
  arm.type = 'button';
  disarm.type = 'button';
  arm.addEventListener('click', () => h.onSend('tof sub=1', 'macro'));
  disarm.addEventListener('click', () => h.onSend('stop', 'macro'));
  const rangeRow = el('div', 'cfg-band');
  rangeRow.append(arm, disarm);

  // --- gps logging --------------------------------------------------------
  const gpsHead = el('div', 'cfg-section', 'Logging');
  const gpsRow = el('label', 'cfg-toggle');
  const gpsBox = el('input');
  gpsBox.type = 'checkbox';
  gpsBox.checked = state.gpsEnabled;
  gpsBox.addEventListener('change', () => h.onToggleGps(gpsBox.checked));
  gpsRow.append(gpsBox, el('span', undefined, 'Log GPS position'));

  const doneBtn = el('button', 'btn', 'Close');
  doneBtn.type = 'button';
  doneBtn.addEventListener('click', close);

  // Gate the send actions on a live connection; GPS toggle is local.
  for (const c of [apply, setDelay, setMax, arm, disarm]) (c as HTMLButtonElement).disabled = !state.connected;
  if (!state.connected) {
    sheet.append(el('div', 'cfg-note', 'Connect the board to apply settings.'));
  }

  sheet.append(
    bandRow,
    freqField,
    grid,
    syncField,
    pwrHint,
    preview,
    warn,
    apply,
    ceilingHead,
    ceilingRow,
    ceilingNote,
    rangingHead,
    delayRow,
    rangeRow,
    gpsHead,
    gpsRow,
    doneBtn,
  );
  render();

  backdrop.append(sheet);
  document.body.append(backdrop);
}
